import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../api/client';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Crown, Loader2, AlertCircle, Search, Building2, Users, Briefcase, DollarSign,
  Ban, RotateCcw, UserCheck, LogIn, ChevronDown, ChevronUp, Megaphone, Send, X,
} from 'lucide-react';
import { toast } from 'sonner';

export default function MasterTenantManager() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [expanded, setExpanded] = useState(null); // company id whose users panel is open
  const [detailUsers, setDetailUsers] = useState({}); // {company_id: [users]}
  const [annOpen, setAnnOpen] = useState(false);
  const [annHistory, setAnnHistory] = useState([]);

  const isMaster = user?.role === 'super_admin';

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get(`/api/master/tenants${q ? `?q=${encodeURIComponent(q)}` : ''}`);
      setTenants(data.tenants || []);
      setTotals(data.platform_totals);
    } catch (e) {
      if (e.response?.status !== 403) toast.error('Failed to load tenants');
    } finally { setLoading(false); }
  };

  useEffect(() => { if (isMaster) load(); else setLoading(false); }, []);

  const loadUsers = async (cid) => {
    try {
      const { data } = await apiClient.get(`/api/master/tenants/${cid}`);
      setDetailUsers((u) => ({ ...u, [cid]: data.users || [] }));
    } catch { toast.error('Could not load users'); }
  };

  const toggleExpand = (cid) => {
    if (expanded === cid) { setExpanded(null); return; }
    setExpanded(cid);
    if (!detailUsers[cid]) loadUsers(cid);
  };

  const suspend = async (cid) => {
    if (!window.confirm('Suspend this tenant? Their users will keep their JWTs until expiry but new logins are blocked at the UI level.')) return;
    try { await apiClient.post(`/api/master/tenants/${cid}/suspend`); toast.success('Tenant suspended'); await load(); }
    catch { toast.error('Failed'); }
  };
  const restore = async (cid) => {
    try { await apiClient.post(`/api/master/tenants/${cid}/restore`); toast.success('Tenant restored'); await load(); }
    catch { toast.error('Failed'); }
  };
  const promote = async (cid, userId) => {
    if (!window.confirm('Promote this user to Company Admin?')) return;
    try { await apiClient.post(`/api/master/tenants/${cid}/promote-admin/${userId}`); toast.success('Promoted to admin'); await loadUsers(cid); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const impersonate = async (userId) => {
    if (!window.confirm('Log in as this user? Your master session will be stored — use the Stop Impersonation button to return.')) return;
    try {
      const { data } = await apiClient.post(`/api/master/impersonate/${userId}`);
      // Stash current master session so we can return
      const currentToken = localStorage.getItem('token');
      const currentUser  = localStorage.getItem('user');
      const currentCo    = localStorage.getItem('company');
      localStorage.setItem('master_session', JSON.stringify({
        token: currentToken, user: currentUser, company: currentCo,
      }));
      // Swap in impersonation creds
      localStorage.setItem('token', data.access_token);
      localStorage.setItem('user', JSON.stringify(data.user));
      localStorage.setItem('company', JSON.stringify(data.company));
      toast.success(`Now logged in as ${data.user.email}`);
      window.location.href = '/';  // hard reload so contexts pick up new auth
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to impersonate'); }
  };

  if (!isMaster) {
    return (
      <div className="space-y-4 fade-in">
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <Crown className="w-7 h-7 text-amber-400" /> Tenant Manager
        </h1>
        <Card><CardContent className="p-6 flex items-start gap-3 text-amber-300">
          <AlertCircle className="w-5 h-5 mt-0.5 shrink-0" />
          <div>
            <p className="font-semibold">Master admin only</p>
            <p className="text-sm text-muted-foreground">This page is reserved for the platform owner.</p>
          </div>
        </CardContent></Card>
      </div>
    );
  }

  const formatCurrency = (a) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(a || 0);

  return (
    <div className="space-y-6 fade-in" data-testid="master-tenant-manager">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2 mb-1">
            <Crown className="w-7 h-7 text-amber-400" /> Tenant Manager
          </h1>
          <p className="text-muted-foreground">Platform-wide control panel. Manage every company.</p>
        </div>
        <Button onClick={() => setAnnOpen(true)} className="btn-gradient-primary" data-testid="send-announcement-button">
          <Megaphone className="w-4 h-4 mr-1" /> Send Announcement
        </Button>
      </div>

      {/* Platform totals */}
      {totals && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat icon={Building2} color="blue"   label="Tenants"   value={totals.tenants} />
          <Stat icon={Users}     color="purple" label="Users"     value={totals.users} />
          <Stat icon={Briefcase} color="green"  label="Jobs"      value={totals.jobs} />
          <Stat icon={DollarSign} color="amber" label="Revenue"   value={formatCurrency(totals.revenue)} />
        </div>
      )}

      {/* Search */}
      <form
        onSubmit={(e) => { e.preventDefault(); load(); }}
        className="flex gap-2"
      >
        <div className="relative flex-1">
          <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by company name, email, or subdomain..."
            className="pl-10"
            data-testid="tenant-search"
          />
        </div>
        <Button type="submit">Search</Button>
        {q && <Button type="button" variant="outline" onClick={() => { setQ(''); setTimeout(load, 0); }}>Clear</Button>}
      </form>

      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
      ) : tenants.length === 0 ? (
        <Card><CardContent className="p-12 text-center text-muted-foreground">No tenants found.</CardContent></Card>
      ) : (
        <div className="grid gap-3">
          {tenants.map((t) => {
            const isSuspended = t.subscription_status === 'suspended';
            const isExpanded  = expanded === t.id;
            return (
              <Card key={t.id} className={isSuspended ? 'border-rose-500/40' : ''} data-testid={`tenant-${t.id}`}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex-1 min-w-[220px]">
                      <div className="flex items-center gap-2 flex-wrap">
                        <CardTitle className="text-lg">{t.name || '(unnamed)'}</CardTitle>
                        <Badge className={
                          isSuspended
                            ? 'bg-rose-500/15 text-rose-300 border-rose-500/40 border'
                            : 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40 border'
                        }>
                          {t.subscription_status || 'active'}
                        </Badge>
                        {t.subscription_plan && (
                          <Badge className="bg-sky-500/15 text-sky-300 border-sky-500/40 border uppercase text-[10px]">
                            {t.subscription_plan}
                          </Badge>
                        )}
                        {t.slug && (
                          <a
                            href={`/login?brand=${t.slug}`}
                            target="_blank"
                            rel="noreferrer"
                            className="text-[11px] text-sky-400 hover:underline font-mono"
                            title="Open branded login"
                          >
                            {t.slug}.mykeyboss.com
                          </a>
                        )}
                      </div>
                      <CardDescription className="text-xs mt-1">
                        {t.email || 'no-email'}{t.phone ? ` · ${t.phone}` : ''}
                      </CardDescription>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      <Button size="sm" variant="outline" onClick={() => toggleExpand(t.id)} data-testid={`expand-${t.id}`}>
                        {isExpanded ? <ChevronUp className="w-3 h-3 mr-1" /> : <ChevronDown className="w-3 h-3 mr-1" />}
                        {isExpanded ? 'Hide users' : 'Show users'}
                      </Button>
                      {isSuspended ? (
                        <Button size="sm" className="btn-gradient-success" onClick={() => restore(t.id)} data-testid={`restore-${t.id}`}>
                          <RotateCcw className="w-3 h-3 mr-1" /> Restore
                        </Button>
                      ) : (
                        <Button size="sm" variant="outline" className="text-rose-400 border-rose-500/40 hover:bg-rose-500/10" onClick={() => suspend(t.id)} data-testid={`suspend-${t.id}`}>
                          <Ban className="w-3 h-3 mr-1" /> Suspend
                        </Button>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-sm">
                    <Mini label="Users" value={t.stats.users} />
                    <Mini label="Customers" value={t.stats.customers} />
                    <Mini label="Jobs (all-time)" value={t.stats.jobs_total} />
                    <Mini label="Jobs (30d)" value={t.stats.jobs_30d} />
                    <Mini label="Revenue (paid)" value={formatCurrency(t.stats.revenue_paid)} />
                  </div>

                  {isExpanded && (
                    <div className="mt-4 pt-4 border-t">
                      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Users</p>
                      {!detailUsers[t.id] ? (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Loader2 className="w-4 h-4 animate-spin" /> Loading users...
                        </div>
                      ) : detailUsers[t.id].length === 0 ? (
                        <p className="text-sm text-muted-foreground">No users yet.</p>
                      ) : (
                        <div className="space-y-1">
                          {detailUsers[t.id].map((u) => (
                            <div key={u.id} className="flex items-center justify-between gap-2 py-1.5 px-2 rounded hover:bg-muted/40">
                              <div className="flex items-center gap-2 min-w-0 flex-1">
                                {u.avatar_url ? (
                                  <img src={u.avatar_url} alt="" className="w-7 h-7 rounded-full object-cover" />
                                ) : (
                                  <div className="w-7 h-7 rounded-full bg-gradient-to-br from-sky-500 to-indigo-500 flex items-center justify-center text-white text-xs font-bold">
                                    {u.first_name?.[0]}{u.last_name?.[0]}
                                  </div>
                                )}
                                <div className="min-w-0">
                                  <p className="text-sm font-medium truncate">{u.first_name} {u.last_name}</p>
                                  <p className="text-[11px] text-muted-foreground truncate">{u.email} · {u.role}</p>
                                </div>
                              </div>
                              <div className="flex gap-1 shrink-0">
                                {u.role !== 'company_admin' && u.role !== 'super_admin' && (
                                  <Button size="sm" variant="outline" onClick={() => promote(t.id, u.id)} title="Promote to Company Admin" data-testid={`promote-${u.id}`}>
                                    <UserCheck className="w-3 h-3" />
                                  </Button>
                                )}
                                <Button size="sm" variant="outline" onClick={() => impersonate(u.id)} title="Log in as this user" data-testid={`impersonate-${u.id}`}>
                                  <LogIn className="w-3 h-3" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {annOpen && (
        <AnnouncementModal
          tenants={tenants}
          onClose={() => setAnnOpen(false)}
          onSent={async () => {
            toast.success('Announcement sent — visible to all tenants now');
            setAnnOpen(false);
            try {
              const { data } = await apiClient.get('/api/master/announcements');
              setAnnHistory(data || []);
            } catch { /* ignore */ }
          }}
          history={annHistory}
          loadHistory={async () => {
            try {
              const { data } = await apiClient.get('/api/master/announcements');
              setAnnHistory(data || []);
            } catch { /* ignore */ }
          }}
        />
      )}
    </div>
  );
}

function AnnouncementModal({ tenants, onClose, onSent, history, loadHistory }) {
  const [message, setMessage] = useState('');
  const [severity, setSeverity] = useState('info');
  const [audience, setAudience] = useState('all');
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);

  React.useEffect(() => { loadHistory(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (message.trim().length < 3) return toast.error('Message is too short');
    if (audience === 'selected' && selected.length === 0) return toast.error('Pick at least one tenant');
    setBusy(true);
    try {
      await apiClient.post('/api/master/announcements', {
        message: message.trim(),
        severity,
        audience,
        company_ids: audience === 'selected' ? selected : null,
      });
      await onSent();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send');
    } finally { setBusy(false); }
  };

  const deactivate = async (id) => {
    try {
      await apiClient.post(`/api/master/announcements/${id}/deactivate`);
      await loadHistory();
      toast.success('Deactivated');
    } catch { toast.error('Failed'); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="bg-card border rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="announcement-modal">
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <div className="flex items-center gap-2">
            <Megaphone className="w-5 h-5 text-sky-400" />
            <h3 className="font-semibold">Send Announcement</h3>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-muted rounded"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Severity</label>
            <div className="grid grid-cols-3 gap-2 mt-1">
              {[
                { id: 'info', label: 'Info', cls: 'border-sky-500/40 text-sky-200 bg-sky-500/10' },
                { id: 'warning', label: 'Warning', cls: 'border-amber-500/40 text-amber-200 bg-amber-500/10' },
                { id: 'critical', label: 'Critical', cls: 'border-rose-500/40 text-rose-200 bg-rose-500/10' },
              ].map((s) => (
                <button
                  type="button" key={s.id}
                  onClick={() => setSeverity(s.id)}
                  className={`text-sm py-2 rounded-md border ${severity === s.id ? s.cls + ' font-semibold' : 'border-input text-muted-foreground hover:bg-muted'}`}
                  data-testid={`severity-${s.id}`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Audience</label>
            <div className="grid grid-cols-2 gap-2 mt-1">
              <button type="button" onClick={() => setAudience('all')} className={`text-sm py-2 rounded-md border ${audience === 'all' ? 'border-sky-500/50 bg-sky-500/10 text-sky-200 font-semibold' : 'border-input text-muted-foreground hover:bg-muted'}`} data-testid="audience-all">
                All tenants
              </button>
              <button type="button" onClick={() => setAudience('selected')} className={`text-sm py-2 rounded-md border ${audience === 'selected' ? 'border-sky-500/50 bg-sky-500/10 text-sky-200 font-semibold' : 'border-input text-muted-foreground hover:bg-muted'}`} data-testid="audience-selected">
                Selected ({selected.length})
              </button>
            </div>
            {audience === 'selected' && (
              <div className="mt-2 max-h-40 overflow-y-auto border rounded p-2 space-y-1">
                {tenants.map((t) => (
                  <label key={t.id} className="flex items-center gap-2 text-sm py-1 cursor-pointer hover:bg-muted/40 rounded px-1">
                    <input
                      type="checkbox"
                      checked={selected.includes(t.id)}
                      onChange={(e) => setSelected((cur) => e.target.checked ? [...cur, t.id] : cur.filter((x) => x !== t.id))}
                    />
                    {t.name} <span className="text-xs text-muted-foreground">({t.stats.users} users)</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Message</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              maxLength={1000}
              rows={4}
              placeholder="e.g. Scheduled maintenance tonight 10pm-11pm EST — app may be briefly unavailable."
              className="mt-1 w-full py-2 px-3 rounded-lg border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 resize-none"
              data-testid="announcement-message"
              required
            />
            <p className="text-[11px] text-muted-foreground mt-1">{message.length}/1000 characters</p>
          </div>

          <Button type="submit" disabled={busy} className="w-full btn-gradient-primary" data-testid="announcement-submit">
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
            Send to {audience === 'all' ? 'all tenants' : `${selected.length} tenant(s)`}
          </Button>
        </form>

        {history.length > 0 && (
          <div className="px-5 pb-5 border-t pt-4">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Recent announcements</p>
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {history.map((h) => (
                <div key={h.id} className="flex items-start justify-between gap-2 text-xs p-2 bg-muted/30 rounded" data-testid={`history-${h.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="truncate">{h.message}</p>
                    <p className="text-muted-foreground mt-0.5">
                      {h.severity} · {h.audience === 'all' ? 'all' : `${h.company_ids?.length || 0} tenants`} · {h.active ? '🟢 active' : '⚫ inactive'} · {new Date(h.created_at).toLocaleString()}
                    </p>
                  </div>
                  {h.active && (
                    <button onClick={() => deactivate(h.id)} className="px-2 py-1 text-xs border border-rose-500/40 text-rose-300 rounded hover:bg-rose-500/10" data-testid={`deactivate-${h.id}`}>
                      Deactivate
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const COLORS = {
  blue:   { card: 'stat-blue',   icon: 'text-blue-500' },
  purple: { card: 'stat-purple', icon: 'text-purple-500' },
  green:  { card: 'stat-green',  icon: 'text-green-500' },
  amber:  { card: 'stat-orange', icon: 'text-amber-500' },
};
function Stat({ icon: Icon, color, label, value }) {
  const c = COLORS[color];
  return (
    <Card className={`${c.card} border`}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-1">
          <Icon className={`w-6 h-6 ${c.icon}`} />
        </div>
        <p className="text-xl font-bold">{value}</p>
        <p className="text-[11px] text-muted-foreground">{label}</p>
      </CardContent>
    </Card>
  );
}
function Mini({ label, value }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="font-semibold">{value}</p>
    </div>
  );
}
